import { guaranteedQuestionHandles } from "@redotech/redo-conversion-hsa-common/hsa-flow-validate";
import {
  HsaConditionStep,
  hsaConditionStepSchema,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { useTranslation } from "@redotech/redo-i18n";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Fragment, memo, useMemo } from "react";
import {
  FrontendStepType,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "..";
import {
  buildHsaQuestionLookup,
  flattenHsaConditionLeaves,
  getHsaConditionLeafAnswerText,
  getHsaConditionLeafQuestionLabel,
} from "../../nodes/hsa-condition-summary";
import { HsaConditionExpressionEditor } from "./hsa-condition-builder";
import { hsaFlowGraphFromSteps } from "./hsa-flow-graph";
import { HsaInspectorShell } from "./hsa-inspector-shell";

type ConditionStep = PartialWithRequiredType<HsaConditionStep>;

const HsaConditionInspector = memo(function HsaConditionInspector(
  props: StepTypeDetailsProps<ConditionStep>,
) {
  const { t } = useTranslation();
  const { step, steps, setStep, onClose, readonly } = props;
  const lookup = useMemo(() => buildHsaQuestionLookup(steps), [steps]);
  const entries = useMemo(
    () => flattenHsaConditionLeaves(step.condition),
    [step.condition],
  );
  // Only questions guaranteed answered on every path to this node may be
  // referenced (dominator analysis; the server re-validates on save).
  const availableHandles = useMemo(
    () =>
      readonly
        ? []
        : guaranteedQuestionHandles(hsaFlowGraphFromSteps(steps), step.id),
    [readonly, steps, step.id],
  );

  return (
    <HsaInspectorShell onClose={onClose} title={t("Condition")}>
      {readonly ? (
        entries.length ? (
          <Flex dir="column" gap="lg">
            {entries.map(({ leaf, connector }, index) => (
              <Fragment key={index}>
                {connector && (
                  <Text
                    fontSize="xxs"
                    fontWeight="semibold"
                    textColor="secondary"
                  >
                    {connector.toUpperCase()}
                  </Text>
                )}
                <Flex dir="column" gap="xxs">
                  <Text fontSize="sm" fontWeight="medium">
                    {getHsaConditionLeafQuestionLabel(leaf, lookup)}
                  </Text>
                  <Text fontSize="xs" textColor="secondary">
                    {t("Matching answer: {{answer}}", {
                      answer:
                        getHsaConditionLeafAnswerText(leaf, lookup) ?? "—",
                    })}
                  </Text>
                </Flex>
              </Fragment>
            ))}
          </Flex>
        ) : (
          <Text fontSize="sm" textColor="secondary">
            {t("No conditions configured.")}
          </Text>
        )
      ) : (
        <HsaConditionExpressionEditor
          availableHandles={availableHandles}
          expression={step.condition}
          lookup={lookup}
          setExpression={(condition) => setStep({ ...step, condition })}
        />
      )}
      <Text fontSize="xs" textColor="secondary">
        {t(
          'Shoppers whose answers match take the "Matches" branch; everyone else takes "Does not match".',
        )}
      </Text>
    </HsaInspectorShell>
  );
});

export const hsaConditionStepType: FrontendStepType<ConditionStep> = {
  type: "custom",
  // The schema allows empty blocks/leaves, but an empty condition matches
  // nothing meaningful — require at least one leaf in every block.
  valid: (step) =>
    hsaConditionStepSchema.safeParse(step).success &&
    (step.condition?.conditionBlocks?.length ?? 0) >= 1 &&
    (step.condition?.conditionBlocks ?? []).every(
      (block) => (block?.conditions?.length ?? 0) >= 1,
    ),
  customInspector: HsaConditionInspector,
};
